<template>
  <div :class="selected_report?.story_board || print_mode ? 'px-print' : 'container'">
    <div v-if="selectedCategoryId && filteredCategories.length">
      <transition name="fade" mode="out-in">
        <div
          v-for="category in filteredCategories"
          :key="category.category_id"
          class="category-container"
        >
          <template v-if="category.category_id === 2 || category.category_id === 3">
            <template v-if="category.category_id === 2">
              <StatementOfUse />
            </template>
            <template v-else>
              <div class="py-4"></div>
            </template>
            <p>Universal Standards</p>
          </template>

          <template v-else>
            <div class="py-4"></div>
            <p>Topic-Specific Disclosures</p>
          </template>

          <h3 :id="!print_mode ? 'rp' + category.category_id : ''" class="py-4 fw-600">
            GRI {{ category.category_id }}.
            {{ lang != 'ENG' ? category.category_name : category.category_name_e }}
          </h3>

          <b-row responsive class="disclosure-table lg mb-2">
            <b-col cols="3" style="min-width: 22%; width: 32%">
              {{ lang != 'ENG' ? '보고 지표' : 'Disclosures' }}
              <HeaderAnchor :active_code="category.category_id" :print_mode="print_mode" />
            </b-col>
            <b-col cols="3" class="d-lg-flex justify-content-between">
              {{ lang != 'ENG' ? '보고 내용' : 'Disclosure Contents' }}
            </b-col>
            <b-col cols="6" class="text-right">
              <b-icon icon="table" class="mr-1" />
              {{ lang != 'ENG' ? '데이터 테이블' : 'Data Tables' }}
              <b-icon icon="file-earmark-text" class="ml-3 mr-1" />
              {{ lang != 'ENG' ? '문서' : 'Files' }} <b-icon icon="link-45deg" class="ml-3 mr-1" />
              {{ lang != 'ENG' ? '외부 링크' : 'URLs' }}
              <b-icon icon="briefcase" class="ml-3 mr-1" />
              {{ lang != 'ENG' ? '비즈니스 사례' : 'Business Cases' }}
            </b-col>
          </b-row>

          <!-- subtopic_code 카테고리 -->
          <div v-if="category.category_id < 200">
            <div
              v-for="(subtopic, index) in getUniqueSubtopicsByCategoryIds(category.category_id)"
              :key="subtopic.subtopic_code"
              :class="{ 'first-subtopic': index === 0, 'new-page-subtopic': index !== 0 }"
              class="mb-5 border-0"
            >
              <b-row>
                <b-col cols="12" class="subtopic-title" :style="main_color_sub">
                  <span :id="!print_mode ? 'rp' + subtopic.subtopic_code : ''" class="fw-700 f-160">
                    GRI {{ subtopic.subtopic_code }}.
                    {{ lang != 'ENG' ? subtopic.subtopic_name : subtopic.subtopic_name_e }}
                  </span>
                  <span class="gray-300 f-100 fw-400 ml-1">{{
                    subtopic.topic_publication_date
                  }}</span>
                </b-col>
              </b-row>

              <div
                v-for="disclosure in getGRIContentsBySubtopicCodes(subtopic.subtopic_code)"
                :id="!print_mode ? 'w' + disclosure.gri_code : ''"
                :key="disclosure.disclosure_id"
                class="border-bottom py-4"
              >
                <b-row>
                  <b-col cols="3">
                    <div class="f-140 fw-400 gray-900">{{ disclosure.gri_code }}</div>
                    <p
                      :id="!print_mode ? 'crp' + disclosure.gri_code : ''"
                      class="f-100 fw-400 gray-900"
                    >
                      {{ lang != 'ENG' ? disclosure.indicator_name : disclosure.indicator_name_e }}
                    </p>
                  </b-col>
                  <b-col v-if="!disclosure.nan_category" cols="9">
                    <md
                      style="word-wrap: break-word"
                      :contents="
                        lang !== 'ENG'
                          ? disclosure.direct_report || ''
                          : disclosure.direct_report_e || ''
                      "
                      :class="{ 'mb-3': disclosure.direct_report }"
                    />

                    <!-- data_tables 넣을자리 -->
                    <div
                      v-for="data_table_id in disclosure.data_tables"
                      :key="'data_table_' + data_table_id"
                    >
                      <DataTable
                        :print_mode="print_mode"
                        :table_mode="table_mode"
                        :data_table_id="data_table_id"
                        :modal_table="modal_table"
                        :lazy_load="false"
                        :table_style="table_style"
                        :show_table_line="show_table_line"
                        :show_table_outline="show_table_outline"
                        :table_header_color="table_header_color"
                        :table_header_font_color="table_header_font_color"
                      />
                    </div>

                    <!-- documents 넣을자리 -->
                    <div class="d-flex flex-column align-items-start">
                      <span
                        v-for="document_id in disclosure.documents"
                        :key="'document_' + document_id"
                      >
                        <Document :document_id="document_id" :lazy_load="false" />
                      </span>
                    </div>

                    <!-- business_cases 넣을자리 -->
                    <div class="d-flex flex-column align-items-start">
                      <span
                        v-for="business_case_id in disclosure.business_cases"
                        :key="'business_case_' + business_case_id"
                      >
                        <BusinessCase
                          :business_case_id="business_case_id"
                          :lazy_load="true"
                          :business_case_mode="business_case_mode"
                        />
                      </span>
                    </div>

                    <div v-for="chart_id in disclosure.charts" :key="'chart_' + chart_id">
                      <ChartCanvas
                        v-if="chart_id !== ''"
                        :key="`chart_${chart_id}`"
                        :print_mode="print_mode"
                        :chart_id="chart_id"
                        :viewer_mode="true"
                        class="mb-5"
                      />
                    </div>

                    <div
                      v-if="
                        disclosure.gri_code === '3-2' &&
                        selected_indicator &&
                        selected_indicator.custom_topic_indicator.length > 0
                      "
                    >
                      <!-- <div v-if="disclosure.gri_code === '3-2'"> -->
                      <b-table-simple
                        responsive
                        :class="`${table_style}` === 'basic_table' ? 'edi-table' : 'no-line-table'"
                        :borderless="!show_table_line"
                        :outlined="show_table_outline"
                      >
                        <b-thead>
                          <b-tr>
                            <b-td>{{ lang !== 'ENG' ? '우선순위' : 'Ranking' }}</b-td>
                            <b-td>{{ lang !== 'ENG' ? '중요 주제명' : 'Topic' }}</b-td>
                            <b-td>{{
                              lang !== 'ENG' ? '중요 주제와 연관된 GRI 지표' : 'GRI Topic Standards'
                            }}</b-td>
                          </b-tr>
                        </b-thead>

                        <b-tbody>
                          <b-tr
                            v-for="(item, idx) in selected_indicator.custom_topic_indicator"
                            :key="idx"
                          >
                            <b-td>
                              {{ idx + 1 }}
                            </b-td>
                            <b-td>
                              {{
                                lang !== 'ENG'
                                  ? item.topic_name
                                  : item.topic_name_em
                                  ? item.topic_name_em
                                  : item.topic_name_ea
                              }}
                            </b-td>
                            <b-td>
                              <span
                                v-for="gri_code in sort_indicator_arr(
                                  item.selected_cm_gri_code_list
                                )"
                                :key="gri_code"
                              >
                                <a href="#" @click="navAnchor($event, gri_code)">{{ gri_code }}</a
                                >&nbsp;
                              </span>

                              <span
                                v-if="
                                  item.selected_cm_gri_code_list &&
                                  item.selected_cm_gri_code_list.length === 0
                                "
                              >
                                Non-GRI
                              </span>
                            </b-td>
                            <hr />
                          </b-tr>
                        </b-tbody>
                      </b-table-simple>
                    </div>
                  </b-col>

                  <b-col v-else cols="9">
                    <div>
                      <p>{{ lang !== 'ENG' ? '보고 생략' : 'Omission in report' }}</p>
                      <p>
                        {{ lang !== 'ENG' ? '사유' : 'Reason' }}: [{{
                          lang !== 'ENG'
                            ? nan_table[disclosure.nan_category]['KOR']
                            : nan_table[disclosure.nan_category]['ENG']
                        }}]
                        {{
                          lang !== 'ENG'
                            ? disclosure.nan_reason_contents
                            : disclosure.nan_reason_contents_em
                            ? disclosure.nan_reason_contents_em
                            : disclosure.nan_reason_contents_ea
                        }}
                      </p>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </div>
          </div>

          <!-- topic_id 카테고리 -->
          <div v-if="category.category_id >= 200">
            <div
              v-for="(topic, index) in getUniqueTopicsByCategoryIds(category.category_id)"
              :key="topic.topic_id"
              :class="{ 'first-topic': index === 0, 'new-page-topic': index !== 0 }"
              class="mb-5 border-0"
            >
              <b-row>
                <b-col cols="12" class="subtopic-title" :style="main_color_sub">
                  <span :id="!print_mode ? 'rp' + topic.topic_id : ''" class="fw-700 f-160">
                    GRI {{ topic.topic_id }}.
                    {{ lang != 'ENG' ? topic.topic_name : topic.topic_name_e }}
                  </span>
                  <span class="gray-300 f-100 fw-400 ml-1">{{ topic.topic_publication_date }}</span>
                </b-col>
              </b-row>

              <div
                v-for="disclosure in getGRIContentsByTopicIds(topic.topic_id)"
                :id="!print_mode ? 'w' + disclosure.gri_code : ''"
                :key="disclosure.disclosure_id"
                class="border-bottom py-4"
              >
                <b-row>
                  <b-col cols="3">
                    <div class="f-140 fw-400 gray-900">{{ disclosure.gri_code }}</div>
                    <p
                      :id="!print_mode ? 'crp' + disclosure.gri_code : ''"
                      class="f-100 fw-400 gray-900"
                    >
                      {{ lang != 'ENG' ? disclosure.indicator_name : disclosure.indicator_name_e }}
                    </p>
                  </b-col>

                  <b-col v-if="!disclosure.nan_category" cols="9">
                    <md
                      style="word-wrap: break-word"
                      :contents="
                        lang !== 'ENG'
                          ? disclosure.direct_report || ''
                          : disclosure.direct_report_e || ''
                      "
                      :class="{ 'mb-3': disclosure.direct_report }"
                    />

                    <!-- data_tables 넣을자리 -->
                    <div
                      v-for="data_table_id in disclosure.data_tables"
                      :key="'data_table_' + data_table_id"
                    >
                      <DataTable
                        :print_mode="print_mode"
                        :table_mode="table_mode"
                        :data_table_id="data_table_id"
                        :modal_table="modal_table"
                        :lazy_load="false"
                        :table_style="table_style"
                        :show_table_line="show_table_line"
                        :show_table_outline="show_table_outline"
                        :table_header_color="table_header_color"
                        :table_header_font_color="table_header_font_color"
                      />
                    </div>

                    <!-- documents 넣을자리 -->
                    <div>
                      <span
                        v-for="document_id in disclosure.documents"
                        :key="'document_' + document_id"
                      >
                        <Document :document_id="document_id" :lazy_load="false" />
                      </span>
                    </div>

                    <!-- business_cases 넣을자리 -->
                    <span
                      v-for="business_case_id in disclosure.business_cases"
                      :key="'business_case_' + business_case_id"
                    >
                      <BusinessCase
                        :business_case_id="business_case_id"
                        :business_case_mode="business_case_mode"
                      />
                    </span>
                    <div v-for="chart_id in disclosure.charts" :key="'chart_' + chart_id">
                      <ChartCanvas
                        v-if="chart_id !== ''"
                        :key="`chart_${chart_id}`"
                        :print_mode="print_mode"
                        :chart_id="chart_id"
                        :viewer_mode="true"
                        class="mb-5"
                      />
                    </div>
                  </b-col>

                  <b-col v-else cols="9">
                    <div>
                      <p>{{ lang !== 'ENG' ? '보고 생략' : 'Omission in report' }}</p>
                      <p>
                        {{ lang !== 'ENG' ? '사유' : 'Reason' }}: [{{
                          lang !== 'ENG'
                            ? nan_table[disclosure.nan_category]['KOR']
                            : nan_table[disclosure.nan_category]['ENG']
                        }}]
                        {{
                          lang !== 'ENG'
                            ? disclosure.nan_reason_contents
                            : disclosure.nan_reason_contents_em
                            ? disclosure.nan_reason_contents_em
                            : disclosure.nan_reason_contents_ea
                        }}
                      </p>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import DataTable from './DataTable.vue';
import Document from './Document.vue';
import BusinessCase from './BusinessCase.vue';
import HeaderAnchor from './HeaderAnchor';
import { nan_table } from '@/components/util';
import StatementOfUse from './StatementOfUse.vue';
import ChartCanvas from '@/components/writing_report/chart_subs/ChartCanvas.vue';
import { gri_mixin } from '@/components/writing_report/write_report_subs/gri_mixin.js';

export default {
  name: 'GRITable2021Micro',
  components: {
    DataTable,
    Document,
    BusinessCase,
    HeaderAnchor,
    StatementOfUse,
    ChartCanvas
  },
  mixins: [gri_mixin],
  props: {
    modal_table: { type: Boolean, default: false },
    reload: { type: Boolean, default: false },
    print_mode: { type: Boolean, default: false },
    business_case_mode: { type: String, default: 'popup' },
    table_mode: { type: String, default: 'accordion' },
    material_style_option: { type: String, default: 'outline' },
    table_style: { type: String, default: 'basic_table' },
    show_table_line: { type: Boolean, default: true },
    show_table_outline: { type: Boolean, default: false },
    table_header_color: { type: String, default: '#dedede' },
    table_header_font_color: { type: String, default: '#fffff' }
  },
  data() {
    return {
      show_data_table: {},
      show_business_case: {},
      low_height: false,
      selected_indicator: null,
      nan_table,
      selectedCategoryId: null
    };
  },
  computed: {
    ...mapState('report_list', ['lang', 'view_mode']),
    ...mapState('gri2021_report_view', ['report_structure', 'report_contents_loaded']),
    ...mapGetters('gri2021_report_view', ['report_categories']),
    ...mapGetters('report_list', ['selected_report', 'current_report_code']),
    filteredCategories() {
      return this.report_categories.filter(c => c.category_id === this.selectedCategoryId);
    },
    main_color() {
      return `color:` + this.selected_report.main_color;
    },
    main_color_sub() {
      if (this.material_style_option === 'fill') {
        return `background-color: ${this.selected_report.main_color}; color: white; border: 1px solid white; `;
      } else if (this.material_style_option === 'outline') {
        return (
          `color:` +
          this.selected_report.main_color +
          `; border-top: 1px solid ` +
          this.selected_report.main_color +
          `!important` +
          `; border-bottom: 1px solid ` +
          this.selected_report.main_color
        );
      } else {
        return `background-color: white; border-left: 3px solid ${this.selected_report.main_color}; border-bottom: 1px solid ${this.selected_report.sub_color}; border-top: 0px; border-right: 0px;`;
      }
    },
    main_bg_color() {
      if (this.selected_report) {
        return `background-color:` + this.selected_report.main_color;
      } else {
        return `background-color: #494949`;
      }
    }
  },
  watch: {
    '$route.query.categoryId': {
      handler(newCategoryId) {
        if (this.report_contents_loaded) {
          this.selectedCategoryId = Number(newCategoryId);
        }
      },
      immediate: true
    }
  },
  async created() {
    await this.selected_gri_code_list();

    const { categoryId } = this.$route.query;
    if (categoryId) {
      this.selectedCategoryId = Number(categoryId);
    }

    if (categoryId) {
      this.$nextTick(() => {
        this.selectedCategoryId = Number(categoryId);
      });
    }
  },
  methods: {
    sort_indicator_arr(arr) {
      return arr.slice().sort((a, b) => {
        const [numA, subNumA] = a.split('-').map(Number);
        const [numB, subNumB] = b.split('-').map(Number);

        if (numA === numB) {
          return subNumA - subNumB;
        } else {
          return numA - numB;
        }
      });
    },

    navAnchor(event, anchor) {
      event.preventDefault();
      if (anchor && document.querySelector('#w' + anchor)) {
        const elem = document.querySelector('#w' + anchor);
        if (elem) {
          elem.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    async selected_gri_code_list() {
      const response = await this.$publicApi.get(
        '/get_selected_gri_indicator_code?rc=' + this.current_report_code
      );
      if (response.data.code) {
        this.selected_indicator = response.data.result;
      }
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to

/* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}

.subtopic-title {
  padding-top: 4px;
  padding-bottom: 4px;
}

@media print {
  .new-page {
    page-break-before: always;
  }

  .first-subtopic {
    page-break-before: avoid;
  }

  .new-page-subtopic {
    page-break-before: always;
  }

  .first-topic {
    page-break-before: avoid;
  }

  .new-page-topic {
    page-break-before: always;
  }
}
</style>
